import React, { useEffect, useMemo, useState } from "react";
import styles from "../../../Handbook.module.css";
import { ActionsMenu } from "../ActionsMenu/ActionsMenu";

export type Column<T> = {
    key: string;
    title: string;
    render?: (row: T) => React.ReactNode;
};

type RowActions = {
    onEdit?: () => void;
    onDelete?: () => void;
};

export type ServerPaginationConfig = {
    totalCount: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    searchValue: string;
    onSearchChange: (value: string) => void;
    searchPlaceholder?: string;
    pageSizeOptions?: number[];
};

type SimpleTableProps<T> = {
    columns: Column<T>[];
    data: T[];

    loading?: boolean;
    error?: string | null;
    emptyText?: string;

    getRowKey?: (row: T) => string;
    getRowActions?: (row: T) => RowActions;

    title: string;
    hideHeader?: boolean;
    setOpen?: () => void;

    isNeedAdd?: boolean;
    isNeedActions?: boolean;
    pageSize?: number;

    enableSearchByName?: boolean;
    getSearchValue?: (row: T) => string;
    /** Подсказка в поле поиска (клиентский и серверный режим) */
    searchPlaceholder?: string;

    /** Серверная пагинация и поиск по API (query `name`) */
    serverPagination?: ServerPaginationConfig;
};

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export function SimpleTable<T>({
    columns,
    data,
    loading = false,
    error = null,
    emptyText = "Нет данных",
    getRowKey,
    getRowActions,
    title,
    hideHeader = false,
    setOpen,
    isNeedAdd = true,
    isNeedActions = true,
    pageSize = 10,
    enableSearchByName = false,
    getSearchValue,
    searchPlaceholder,
    serverPagination,
}: SimpleTableProps<T>) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");

    const isServerMode = Boolean(serverPagination);

    const filteredData = useMemo(() => {
        if (isServerMode) {
            return data ?? [];
        }
        if (!enableSearchByName || !search.trim()) {
            return data ?? [];
        }

        const needle = search.toLowerCase();

        return (data ?? []).filter((row) => {
            const raw = getSearchValue ? getSearchValue(row) : (row as any)?.name;

            if (raw == null) return false;

            return String(raw).toLowerCase().includes(needle);
        });
    }, [data, enableSearchByName, getSearchValue, search, isServerMode]);

    const effectivePageSize = isServerMode ? serverPagination!.pageSize : pageSize;

    const totalItems = isServerMode ? serverPagination!.totalCount : filteredData.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / effectivePageSize) || 1);

    const safePage = isServerMode
        ? Math.min(Math.max(1, serverPagination!.page), totalPages)
        : Math.min(page, totalPages);

    const paginatedData = useMemo(() => {
        if (isServerMode) {
            return filteredData;
        }
        const startIndex = (safePage - 1) * effectivePageSize;
        const endIndex = startIndex + effectivePageSize;
        return filteredData.slice(startIndex, endIndex);
    }, [filteredData, isServerMode, safePage, effectivePageSize]);

    useEffect(() => {
        if (!isServerMode) {
            setPage(1);
        }
    }, [search, isServerMode]);

    const showEmpty = !loading && !error && paginatedData.length === 0;

    const handlePrev = () => {
        if (isServerMode) {
            serverPagination!.onPageChange(Math.max(1, serverPagination!.page - 1));
        } else {
            setPage((prev) => Math.max(1, prev - 1));
        }
    };

    const handleNext = () => {
        if (isServerMode) {
            serverPagination!.onPageChange(Math.min(totalPages, serverPagination!.page + 1));
        } else {
            setPage((prev) => Math.min(totalPages, prev + 1));
        }
    };

    const showSearch = enableSearchByName || isServerMode;
    const searchInputValue = isServerMode ? serverPagination!.searchValue : search;
    const onSearchInputChange = (v: string) => {
        if (isServerMode) {
            serverPagination!.onSearchChange(v);
        } else {
            setSearch(v);
        }
    };

    const resolvedSearchPlaceholder =
        (isServerMode && serverPagination?.searchPlaceholder) ||
        searchPlaceholder ||
        "Поиск по названию";

    const rangeStart = totalItems === 0 ? 0 : (safePage - 1) * effectivePageSize + 1;
    const rangeEnd = totalItems === 0 ? 0 : Math.min(safePage * effectivePageSize, totalItems);

    const pageSizeOptions = isServerMode
        ? serverPagination?.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS
        : DEFAULT_PAGE_SIZE_OPTIONS;

    const showClientPagination = !isServerMode && totalPages > 1;

    return (
        <>
            {!hideHeader && (
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionTitle}>{title}</div>

                    <div className={styles.toolbarRight}>
                        {showSearch && (
                            <div className={styles.searchWrap}>
                                <span className={styles.searchIcon}>🔍</span>
                                <input
                                    className={styles.searchInput}
                                    type="text"
                                    placeholder={resolvedSearchPlaceholder}
                                    value={searchInputValue}
                                    onChange={(e) => onSearchInputChange(e.target.value)}
                                />
                            </div>
                        )}

                        {isNeedAdd && (
                            <button
                                className={styles.addButton}
                                onClick={setOpen}
                                type="button"
                            >
                                Добавить
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className={styles.tableCard}>
                {loading || error || showEmpty ? (
                    <div className={styles.tableState}>
                        {loading && (
                            <>
                                <div className={styles.tableStateTitle}>Загрузка…</div>
                                <div className={styles.tableStateHint}>
                                    Пожалуйста, подождите
                                </div>
                            </>
                        )}

                        {!loading && error && (
                            <>
                                <div className={styles.tableStateTitle}>Ошибка</div>
                                <div className={styles.tableStateError}>{error}</div>
                            </>
                        )}

                        {!loading && !error && showEmpty && (
                            <>
                                <div className={styles.tableStateTitle}>{emptyText}</div>
                                <div className={styles.tableStateHint}>
                                    Попробуйте изменить поиск
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <>
                        <div className={styles.tableScroll}>
                            <table className={styles.table}>
                                <thead className={styles.thead}>
                                <tr>
                                    {columns.map((c) => (
                                        <th key={c.key} className={styles.th}>
                                            {c.title}
                                        </th>
                                    ))}
                                    {isNeedActions && (
                                        <th className={`${styles.th} ${styles.actionsCell}`} />
                                    )}
                                </tr>
                                </thead>

                                <tbody>
                                {paginatedData.map((row, idx) => {
                                    const actions = getRowActions?.(row);

                                    return (
                                        <tr
                                            key={getRowKey ? getRowKey(row) : idx}
                                            className={styles.tr}
                                        >
                                            {columns.map((c) => (
                                                <td key={c.key} className={styles.td}>
                                                    {c.render
                                                        ? c.render(row)
                                                        : String((row as any)[c.key] ?? "")}
                                                </td>
                                            ))}

                                            {isNeedActions && (
                                                <td
                                                    className={`${styles.td} ${styles.actionsCell}`}
                                                >
                                                    <ActionsMenu
                                                        onEdit={actions?.onEdit}
                                                        onDelete={actions?.onDelete}
                                                    />
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>

                        {showClientPagination && (
                            <div className={styles.pagination}>
                                <span className={styles.pageInfo}>
                                    {rangeStart}–{rangeEnd} из {totalItems}
                                </span>
                                <div className={styles.paginationControls}>
                                    <button
                                        type="button"
                                        className={styles.pageButton}
                                        onClick={handlePrev}
                                        disabled={safePage === 1}
                                    >
                                        Назад
                                    </button>

                                    <span className={styles.pageInfo}>
                                        Страница {safePage} из {totalPages}
                                    </span>

                                    <button
                                        type="button"
                                        className={styles.pageButton}
                                        onClick={handleNext}
                                        disabled={safePage === totalPages}
                                    >
                                        Вперед
                                    </button>
                                </div>
                            </div>
                        )}

                        {isServerMode && (
                            <div className={styles.pagination}>
                                <span className={styles.pageInfo}>
                                    {rangeStart}–{rangeEnd} из {totalItems}
                                </span>
                                <div className={styles.paginationControls}>
                                    {serverPagination!.onPageSizeChange && (
                                        <select
                                            className={styles.pageSizeSelect}
                                            value={serverPagination!.pageSize}
                                            onChange={(e) =>
                                                serverPagination!.onPageSizeChange!(
                                                    Number(e.target.value)
                                                )
                                            }
                                            aria-label="Размер страницы"
                                        >
                                            {pageSizeOptions.map((n) => (
                                                <option key={n} value={n}>
                                                    По {n}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    <button
                                        type="button"
                                        className={styles.pageButton}
                                        onClick={handlePrev}
                                        disabled={safePage === 1}
                                    >
                                        Назад
                                    </button>

                                    <span className={styles.pageInfo}>
                                        Страница {safePage} из {totalPages}
                                    </span>

                                    <button
                                        type="button"
                                        className={styles.pageButton}
                                        onClick={handleNext}
                                        disabled={safePage === totalPages}
                                    >
                                        Вперед
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
}
